import type {
  KundaliRequest,
  KundaliResponse,
  DoshaResult,
  PlanetPosition,
  HouseData,
  RashiInfo,
  MatchingRequest,
  MatchingResponse,
  PersonDetails,
  ProductRecommendation,
  RashifalResponse,
  DoshaFinding,
  DoshaCrosscheckResult,
  NavamshaResult,
  MatchingDashaResult,
  DetailedMatchingResponse,
} from "@ishvaram/api-client";
import { getGoogleAnalyticsClientId } from "@/lib/analytics-ga4";
import { getOrCreateBootstrapDistinctId as getAnalyticsDistinctId } from "@/lib/analytics-identity";
import { API_BASE, fetchWithTimeout, throwApiError } from "./_fetch";
import {
  newIdempotencyKey,
  RetryableHttpError,
  withResilientRetry,
  type RetryAttemptOutcome,
} from "./_retry";

export type {
  KundaliRequest,
  KundaliResponse,
  DoshaResult,
  PlanetPosition,
  HouseData,
  RashiInfo,
  MatchingRequest,
  MatchingResponse,
  PersonDetails,
  ProductRecommendation,
  RashifalResponse,
  DoshaFinding,
  DoshaCrosscheckResult,
  NavamshaResult,
  DetailedMatchingResponse,
};
export type { KootaResult, MangalDoshaResult, PersonInput } from "@ishvaram/api-client";

// DashaProblemPeriod / DashaResult (matching context) — re-exported under
// web-local names for backwards compat; MatchingDashaResult has the concrete shape.
export type DashaProblemPeriod = MatchingDashaResult["problem_periods"][number];
export type { MatchingDashaResult as DashaResult };

const MAX_KUNDALI_ATTEMPTS = 3;

export type { RetryAttemptOutcome } from "./_retry";
export { RetryExhaustedError } from "./_retry";

export interface GenerateKundaliOptions {
  /**
   * One idempotency key per logical browser submit. Reused across every retry
   * attempt so a flaky-network re-send cannot double-create the reading
   * server-side. A manual "Try again" should pass the SAME key. Defaults to a
   * fresh key when omitted (single-shot callers).
   */
  idempotencyKey?: string;
  /**
   * Per-attempt telemetry (rule 34 owned beacon at the call site). Fires once
   * per attempt so the field funnel can tell "network unreachable" (retry_network)
   * from "bad input" (retry_4xx_skip) from success (ok).
   */
  onAttempt?: (attempt: number, outcome: RetryAttemptOutcome) => void;
}

/**
 * Generate a kundali with the resilient-submit standard (`_retry.ts`):
 * full-jitter exponential backoff, one idempotency key reused across attempts,
 * 4xx never retried, and a typed `RetryExhaustedError` on exhaustion so the UI
 * renders a user-facing "Try again" state instead of a dead-end string.
 *
 * 30s timeout per attempt because /kundali does Swiss Ephemeris + Sarvam LLM
 * (18-22s cold-start). 2026-05 funnel data showed conversion dropping from 83%
 * to 0% when the bulk of failures surfaced as native "TypeError: Failed to fetch"
 * from Indian ISPs flaking on direct calls — full jitter spreads those retries.
 */
export async function generateKundali(
  data: KundaliRequest,
  options: GenerateKundaliOptions = {}
): Promise<KundaliResponse> {
  const idempotencyKey = options.idempotencyKey ?? newIdempotencyKey();
  return withResilientRetry<KundaliResponse>(
    async (key) => {
      const res = await fetchWithTimeout(
        `${API_BASE}/kundali`,
        {
          method: "POST",
          headers: { ...kundaliAnalyticsHeaders(), "Idempotency-Key": key },
          body: JSON.stringify(data),
        },
        30_000
      );
      if (!res.ok) {
        // 4xx → terminal (throwApiError surfaces the field message); 5xx →
        // RetryableHttpError so withResilientRetry backs off and re-sends.
        if (res.status >= 400 && res.status < 500) await throwApiError(res);
        throw new RetryableHttpError(res.status, `kundali ${res.status}`);
      }
      return res.json() as Promise<KundaliResponse>;
    },
    {
      maxAttempts: MAX_KUNDALI_ATTEMPTS,
      idempotencyKey,
      onAttempt: options.onAttempt,
    }
  );
}

/**
 * Fetch a previously saved reading by id (GET /kundali/{reading_id}) to reuse
 * the birth details the seeker already entered — e.g. the agentic cart
 * prefilling its report form instead of re-asking. Best-effort: callers treat a
 * rejection as "no prefill, the user types it".
 */
export async function getSavedKundali(readingId: string): Promise<KundaliResponse> {
  const res = await fetchWithTimeout(
    `${API_BASE}/kundali/${encodeURIComponent(readingId)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
    15_000
  );
  if (!res.ok) throw new Error(`Could not load saved reading (${res.status})`);
  return res.json() as Promise<KundaliResponse>;
}

function kundaliAnalyticsHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (typeof window === "undefined") return headers;
  const posthogDistinctId = getAnalyticsDistinctId();
  const gaClientId = getGoogleAnalyticsClientId();
  if (posthogDistinctId) headers["X-PostHog-Distinct-Id"] = posthogDistinctId;
  if (gaClientId) headers["X-GA-Client-Id"] = gaClientId;
  headers["X-Ishvaram-Source-Path"] = window.location.pathname || "/free-kundali";
  return headers;
}

export async function getRashifal(
  rashi: string,
  period = "daily",
  language = "en"
): Promise<RashifalResponse> {
  const qp = new URLSearchParams();
  if (period !== "daily") qp.set("period", period);
  if (language !== "en") qp.set("language", language);
  const qs = qp.toString();
  // Monthly/weekly LLM generation takes longer — give them 40s vs the default 12s
  const timeoutMs = period === "monthly" ? 40_000 : period === "weekly" ? 30_000 : 12_000;
  const res = await fetchWithTimeout(
    `${API_BASE}/rashifal/${rashi}${qs ? `?${qs}` : ""}`,
    {},
    timeoutMs
  );
  if (!res.ok) throw new Error(`Failed to fetch rashifal for ${rashi}`);
  return res.json();
}

export async function listRashis(): Promise<RashiInfo[]> {
  const res = await fetchWithTimeout(`${API_BASE}/rashifal`);
  if (!res.ok) throw new Error("Failed to fetch rashis");
  return res.json();
}

export async function calculateMatching(data: MatchingRequest): Promise<MatchingResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/matching`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function calculateDetailedMatching(
  data: MatchingRequest
): Promise<DetailedMatchingResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/matching/detailed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function downloadMatchingReport(
  data: MatchingRequest & { language?: string }
): Promise<Blob> {
  const res = await fetchWithTimeout(`${API_BASE}/reports/matching`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      person_a: data.person_a,
      person_b: data.person_b,
      language: data.language || "en",
    }),
  });
  if (!res.ok) await throwApiError(res, "Report generation failed");
  return res.blob();
}
